import {type Camera, Matrix4, Quaternion, Vector3,} from 'three';
import type {StandardViewCameraFrame} from '../view-orientation/standardViewTypes';


/** 相机动画器依赖的最小宿主端口。 */
export interface ViewportGizmoCameraAnimationHost {
  /** 获取当前活动相机。 */
  readonly getCamera: () => Camera;
  /** 获取可变观察目标。 */
  readonly getTarget: () => Vector3;
  /** 读取默认导航控制器启用状态。 */
  readonly isNavigationEnabled: () => boolean;
  /** 设置默认导航控制器启用状态。 */
  readonly setNavigationEnabled: (enabled: boolean) => void;
  /** 相机姿态写入后同步默认控制器。 */
  readonly syncNavigation: () => void;
}

/** 导航器相机动画完成回调。 */
export type ViewportGizmoCameraAnimationCompletion = () => void;

/** 使用四元数球面插值平滑应用导航器相机帧。 */
export class ViewportGizmoCameraAnimator {
  /** 默认动画持续时间。 */
  private static readonly DEFAULT_DURATION = 360;

  /** 动画宿主。 */
  private readonly host: ViewportGizmoCameraAnimationHost;

  /** 起始相机四元数。 */
  private readonly startQuaternion = new Quaternion();

  /** 目标相机四元数。 */
  private readonly endQuaternion = new Quaternion();

  /** 起始观察目标。 */
  private readonly startTarget = new Vector3();

  /** 目标观察目标。 */
  private readonly endTarget = new Vector3();

  /** 目标相机位置。 */
  private readonly endPosition = new Vector3();

  /** 目标相机 up。 */
  private readonly endUp = new Vector3();

  /** 当前插值观察目标。 */
  private readonly interpolatedTarget = new Vector3();

  /** 当前插值观察方向。 */
  private readonly viewDirection = new Vector3();

  /** 临时旋转矩阵。 */
  private readonly rotationMatrix = new Matrix4();

  /** 起始相机距离。 */
  private startDistance = 0;

  /** 目标相机距离。 */
  private endDistance = 0;

  /** 动画开始时间。 */
  private startTime = 0;

  /** 动画持续时间。 */
  private duration = ViewportGizmoCameraAnimator.DEFAULT_DURATION;

  /** 动画前默认导航状态。 */
  private navigationEnabledBeforeAnimation = true;

  /** 动画完成回调。 */
  private completion: ViewportGizmoCameraAnimationCompletion | null = null;

  /** 当前是否正在动画。 */
  private animating = false;

  /**
   * 创建相机动画器。
   *
   * @param host 相机、目标和默认控制器端口。
   */
  public constructor(host: ViewportGizmoCameraAnimationHost) {
    this.host = host;
  }

  /** 当前是否存在未完成动画。 */
  public get isAnimating(): boolean {
    return this.animating;
  }

  /**
   * 从当前姿态开始过渡到目标相机帧。
   *
   * @param frame 目标位置、目标点和 up。
   * @param completion 完成后执行的后处理。
   * @param duration 动画毫秒数。
   */
  public start(
    frame: StandardViewCameraFrame,
    completion: ViewportGizmoCameraAnimationCompletion | null = null,
    duration: number = ViewportGizmoCameraAnimator.DEFAULT_DURATION,
  ): void {
    const camera = this.host.getCamera();
    const target = this.host.getTarget();

    // 连续触发动画时保留第一段动画开始前的导航状态，避免最终错误启用控制器。
    if (!this.animating) {
      this.navigationEnabledBeforeAnimation = this.host.isNavigationEnabled();
    }
    this.host.setNavigationEnabled(false);
    this.startQuaternion.copy(camera.quaternion);
    this.startTarget.copy(target);
    this.startDistance = camera.position.distanceTo(target);
    this.endPosition.set(...frame.position);
    this.endTarget.set(...frame.target);
    this.endUp.set(...frame.up).normalize();
    this.endDistance = this.endPosition.distanceTo(this.endTarget);
    this.rotationMatrix.lookAt(this.endPosition, this.endTarget, this.endUp);
    this.endQuaternion.setFromRotationMatrix(this.rotationMatrix);
    this.startTime = performance.now();
    this.duration = Math.max(1, duration);
    this.completion = completion;
    this.animating = true;
  }

  /**
   * 按当前时间推进动画；由 Viewer 固定帧循环调用。
   *
   * @param timestamp 可注入的高精度时间，默认读取 performance.now()。
   */
  public update(timestamp: number = performance.now()): void {
    if (!this.animating) {
      return;
    }

    const camera = this.host.getCamera();
    const target = this.host.getTarget();
    const ratio = Math.min(
      Math.max((timestamp - this.startTime) / this.duration, 0),
      1,
    );
    const easedRatio = this.easeInOutCubic(ratio);
    this.interpolatedTarget.lerpVectors(
      this.startTarget,
      this.endTarget,
      easedRatio,
    );
    target.copy(this.interpolatedTarget);
    camera.quaternion.slerpQuaternions(
      this.startQuaternion,
      this.endQuaternion,
      easedRatio,
    );
    this.rotationMatrix.makeRotationFromQuaternion(camera.quaternion);
    camera.up.setFromMatrixColumn(this.rotationMatrix, 1).normalize();

    // 姿态插值后沿当前观察方向恢复插值距离，避免相机线性穿过目标点。
    const distance = this.startDistance
      + (this.endDistance - this.startDistance) * easedRatio;
    camera.getWorldDirection(this.viewDirection);
    camera.position.copy(target).addScaledVector(this.viewDirection, -distance);
    camera.updateMatrixWorld(true);

    if (ratio >= 1) {
      this.complete();
    }
  }

  /** 取消动画并恢复进入动画前的导航状态。 */
  public cancel(): void {
    if (!this.animating) {
      return;
    }

    this.animating = false;
    this.completion = null;
    this.host.setNavigationEnabled(this.navigationEnabledBeforeAnimation);
    this.host.syncNavigation();
  }

  /** 写入目标精确姿态并完成动画生命周期。 */
  private complete(): void {
    const camera = this.host.getCamera();
    const target = this.host.getTarget();
    target.copy(this.endTarget);
    camera.position.copy(this.endPosition);
    camera.up.copy(this.endUp);
    camera.lookAt(target);
    camera.updateMatrixWorld(true);
    this.animating = false;
    this.host.setNavigationEnabled(this.navigationEnabledBeforeAnimation);
    this.host.syncNavigation();

    const completion = this.completion;
    this.completion = null;
    completion?.();
  }

  /**
   * 计算三次缓入缓出比例。
   *
   * @param value 线性插值比例。
   * @returns 三次缓动后的比例。
   */
  private easeInOutCubic(value: number): number {
    return value < 0.5
      ? 4 * value * value * value
      : 1 - Math.pow(-2 * value + 2, 3) / 2;
  }
}

